import { useEffect, useState } from "react";
import { X, Zap, Calendar, User, ThumbsUp, ThumbsDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Iter389 · Feature 3 — Owner-side pending early-access-request panel.
// Lists everything in status='pending', with per-row Approve/Decline
// actions. Optional override_hours field lets the Owner grant custom
// window in one action; else the default global hours apply.
export default function EarlyAccessRequestsPanel({ festivalId = null }) {
  const [items, setItems] = useState(null);
  const [unlockAt, setUnlockAt] = useState({}); // req_id -> datetime-local str
  const [reason, setReason] = useState({});
  const [busy, setBusy] = useState({});

  const reload = async () => {
    try {
      const r = await api.get("/admin/early-access-requests");
      setItems((r.data.items || []).filter((it) => !festivalId || it.festival_id === festivalId));
    } catch { setItems([]); }
  };
  useEffect(() => { reload(); }, []);

  const decide = async (r, approve) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const body = { approve };
      if (approve) {
        if (!unlockAt[r.id]) { toast.error("Pick an early access start time first"); return; }
        body.unlock_at = new Date(unlockAt[r.id]).toISOString();
      }
      if (!approve && reason[r.id]) body.reason = reason[r.id];
      await api.post(`/admin/early-access-requests/${r.id}/decide`, body);
      toast.success(approve ? "Approved — organiser notified" : "Declined — organiser notified");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy((b) => ({ ...b, [r.id]: false }));
    }
  };

  if (!items || items.length === 0) return null;
  return (
    <div className="card-surface p-5 mb-6 border-l-4 border-[#FFB020]" data-testid="ea-requests-panel">
      <div className="label-mono text-[#FFB020] mb-3">EARLY ACCESS · PENDING REQUESTS ({items.length})</div>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="border border-[#262626] p-3 bg-[#0A0A0A]" data-testid={`ea-req-${r.id}`}>
            <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold text-white truncate">{r.festival_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#525252] mt-0.5">
                  <User className="w-2.5 h-2.5 inline mr-1" />
                  {r.organiser_name || r.organiser_email}
                  <span className="ml-2 text-[#66C2FF]">current: {r.current_hours}h</span>
                </div>
              </div>
              <span className="text-[10px] text-[#525252]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <p className="text-xs text-[#A3A3A3] mb-2 italic border-l-2 border-[#66C2FF] pl-2" data-testid={`ea-req-reason-${r.id}`}>
              "{r.reason}"
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                Early access from
                <input
                  type="datetime-local"
                  value={unlockAt[r.id] || ""}
                  onChange={(e) => setUnlockAt((s) => ({ ...s, [r.id]: e.target.value }))}
                  data-testid={`ea-req-unlock-at-${r.id}`}
                  className="bg-[#141414] border border-[#262626] px-2 py-1 text-white text-xs focus:border-[#CCFF00] focus:outline-none [color-scheme:dark]"
                />
              </label>
              <button
                type="button"
                onClick={() => decide(r, true)}
                disabled={busy[r.id] || !unlockAt[r.id]}
                title={!unlockAt[r.id] ? "Pick an early access start time to approve" : undefined}
                data-testid={`ea-req-approve-${r.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest bg-[#CCFF00] text-black hover:opacity-80 disabled:opacity-40"
              >
                <ThumbsUp className="w-3 h-3" /> Approve
              </button>
              <input
                type="text" maxLength={300} placeholder="Decline reason"
                value={reason[r.id] || ""}
                onChange={(e) => setReason((s) => ({ ...s, [r.id]: e.target.value }))}
                data-testid={`ea-req-reason-input-${r.id}`}
                className="flex-1 min-w-[160px] bg-[#141414] border border-[#262626] px-2 py-1 text-white text-xs focus:border-[#FF4500] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => decide(r, false)}
                disabled={busy[r.id]}
                data-testid={`ea-req-decline-${r.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black disabled:opacity-40"
              >
                <ThumbsDown className="w-3 h-3" /> Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
